<template>
  <div class="roxxrt_container">
    <div class="roxxrt_container__flex">
      <!--
        提携先ロゴ。DL 送客数を増やすため既定は文言のみ。
        autoRtLogoVisibleGate または foreverHistoryGate が true の行だけロゴを出す。
      -->
      <!-- キャリコネは①の連携先ロゴ × yagish ロゴを組み立てて出す（媒体ごとの合成画像は作らない） -->
      <CareerconnectionMediaLogo
        v-if="showLogo && info.ccAllianceKey"
        class="roxxrt_img roxxrt_img--cc"
        :media-logo="info.ccMediaLogo"
        :media-name="info.ccMediaName"
        :fallback-src="info.logoImage"
        @load="onLogoLoad"
      />
      <img
        v-else-if="showLogo && info.logoImage"
        :src="info.logoImage"
        alt="Yagish*"
        class="roxxrt_img"
        @load="onLogoLoad"
      />
      <p class="roxxrt_txt">
        <template v-if="!info.hide_yagish_terms">
          Yagishの<router-link
            id="footer-link-terms"
            :to="{ name: 'termsPage' }"
            target="_blank"
            >利用規約</router-link
          >、<router-link
            id="footer-link-policy"
            :to="{ name: 'policyPage' }"
            target="_blank"
            >個人情報保護方針</router-link
          >、<a href="https://www.yagish.jp/privacy_policy/" target="_blank"
            >プライバシーポリシー</a
          ><br />
        </template>
        <span v-if="hasPartnerTerms" v-html="partnerTermsHtml" />
        <template v-else>
          {{ info.clientName }}の<a :href="info.termurl" target="_blank"
            >利用規約</a
          >、<a :href="info.privacyurl" target="_blank">{{
            info.privacyLabel || '個人情報保護方針'
          }}</a
          ><a v-if="info.handlingurl" :href="info.handlingurl" target="_blank"
            >、個人情報の取扱い</a
          ><a
            v-if="info.occupationurl"
            :href="info.occupationurl"
            target="_blank"
            >、取扱職種の範囲等の明示</a
          >を確認して{{ info.clientServiceName }}に登録することになります。
        </template>
        <br v-if="info.showTrailingBr" />
        <!-- PC は 1 行が長くなるので、※ の前で必ず改行する（roxxrt_note） -->
        <span class="roxxrt_note"
          >※ご経験やスキルに応じて求人をご案内する場合があります</span
        >
      </p>
    </div>
    <p class="roxxrt_container__txt">
      <el-checkbox
        class="rt-agree-checkbox"
        :value="agreed"
        @change="emit('update:agreed', $event === true)"
      />
      &#x3000;規約に同意して無料相談を希望する
    </p>
  </div>
</template>

<script setup lang="ts">
/**
 * auto_rt（完了画面の自動送客）の同意ブロック。DownloadItem.vue から文言ごと移設した。
 * 表示条件・同意状態の正本は useCompletedOutput 側（このコンポーネントは presentational）。
 */
import CareerconnectionMediaLogo from '@/components/realtime-transfer/CareerconnectionMediaLogo.vue'
import { computed } from 'vue'
import { reportImageCacheStat } from '@/utils/image-cache-stats'

const props = defineProps<{
  /** autoRtState.info（クライアント名・規約 URL など） */
  info: Record<string, any>
  hasPartnerTerms: boolean
  partnerTermsHtml: string
  agreed: boolean
}>()

const emit = defineEmits<{ (e: 'update:agreed', value: boolean): void }>()

/**
 * additional_dialog_info.autoRtLogoVisibleGate / foreverHistoryGate
 * （buildAutoRtDialogInfo で boolean に詰め替え済み）
 */
const showLogo = computed(
  () =>
    props.info.autoRtLogoVisibleGate === true ||
    props.info.foreverHistoryGate === true
)

/** ロゴがキャッシュ/SW/ネットワークのどこから来たかを Whatap へ送る（DownloadItem から移設） */
function onLogoLoad(event: Event) {
  reportImageCacheStat(event.target as HTMLImageElement, {
    dialogName: 'auto_rt',
    clientId: props.info.clientId,
    permissionType: props.info.permissionType
  })
}
</script>

<style lang="scss" scoped>
.roxxrt_container {
  text-align: center;

  // 同意行。下の出力タイルより目立たないよう、規約文の一段上に留める
  font-size: 13px;

  @media (width < 768px) {
    font-size: 12px;
  }

  &__txt {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    line-height: 1.4;

    // 同意行（15 / 14px）に揃える。以前の scale(1.4) は 11px 文言に対して大きすぎた
    .rt-agree-checkbox {
      transform: scale(1.15);
      transform-origin: center;
      margin-right: 2px;

      @media (width < 768px) {
        transform: scale(1.1);
      }

      // チェック状態の色をプロジェクトテーマのピンクからグレーに上書き（scoped 配下のため :deep()）
      :deep(.el-checkbox__input.is-checked .el-checkbox__inner),
      :deep(.el-checkbox__input.is-indeterminate .el-checkbox__inner) {
        background-color: #909399;
        border-color: #909399;
      }

      // フォーカス時の枠線色もグレーに合わせる
      :deep(.el-checkbox__input.is-focus .el-checkbox__inner) {
        border-color: #909399;
      }

      // ラベル文字色がチェック時にピンクへ変わる挙動も無効化
      :deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
        color: inherit;
      }
    }
  }

  &__flex {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;

    // ロゴの上下は詰める（ロゴ画像自体にも上下数 px の余白がある）
    gap: 4px;
    padding: 0;
    width: 100%;
    margin: 0 auto;

    // 提携ロゴは横長（おおむね 6:1）。幅 220px だと高さ約 35px まで潰れる。
    // RT ダイアログのロゴ上限（高さ 80px）に揃え、スマホはカード幅いっぱいで約 2 倍。
    .roxxrt_img {
      flex: 0 1 auto;
      min-width: 0;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 56px;
      object-fit: contain;

      @media (width < 768px) {
        max-height: 44px;
      }

      &--cc {
        width: 100%;
        max-width: 420px;
        max-height: none;

        --cc-logo-height: 44px;
        --cc-logo-times: 20px;
        --cc-logo-static-max-height: 56px;

        @media (width < 768px) {
          max-width: 100%;

          --cc-logo-height: 36px;
          --cc-logo-times: 16px;
          --cc-logo-static-max-height: 44px;
        }
      }
    }

    .roxxrt_txt {
      width: 100%;
      text-align: center;
      font-size: 11px;
      line-height: 1.5;

      @media (width < 768px) {
        font-size: 10px;
      }
    }

    .roxxrt_note {
      @media (width >= 768px) {
        display: block;
      }
    }
  }
}
</style>
